import type { ReactNode } from "react";
import type { FAQItem } from "@/lib/schema";
import { Action, type ActionLink } from "./ConversionPage.Action";

export type { ActionLink };

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  subtitle: ReactNode;
  trustSignals?: string[];
  primaryAction?: ActionLink;
  secondaryAction?: ActionLink;
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  trustSignals = [],
  primaryAction,
  secondaryAction,
}: PageHeroProps) {
  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 pt-8 sm:pt-12 pb-8 text-center">
      {/* Hero text block is read-only chrome — PostHog logged 19+21 dead
          clicks/wk on the H1 / subtitle paragraph as users tapped expecting
          scroll-to-form (native mobile pattern). The primaryAction button
          below already handles that affordance; mark the text inert so the
          cursor stays default and the click doesn't register as dead. Text
          remains selectable for copy-paste. */}
      <div style={{ pointerEvents: "none" }}>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[var(--ish-accent)] select-text">
          {eyebrow}
        </p>
        <h1 className="mt-3 text-[32px] sm:text-[44px] font-bold leading-[1.08] text-[var(--ish-dark)] select-text">
          {title}
        </h1>
        <div className="mx-auto mt-4 max-w-2xl text-[15px] sm:text-[16px] leading-[1.7] text-[var(--ish-text-muted)] select-text">
          {subtitle}
        </div>
      </div>

      {trustSignals.length > 0 ? (
        // Trust badges are pure descriptive chrome — bullet+text combo reads as
        // tappable but never was. pointer-events:none stops the cursor-pointer
        // and silences the dead-click stream PostHog kept logging on
        // /ai-astrologer (Free to start: 440+/wk on the old badge layout).
        // Badges stay visually identical and selectable for copy-paste.
        <div
          className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[12px] font-semibold text-[var(--ish-text-faint)]"
          style={{ pointerEvents: "none" }}
        >
          {trustSignals.map((signal) => (
            <span key={signal} className="inline-flex items-center gap-1.5 select-text">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--ish-accent)]" aria-hidden />
              {signal}
            </span>
          ))}
        </div>
      ) : null}

      {primaryAction || secondaryAction ? (
        <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
          {primaryAction ? <Action action={primaryAction} primary /> : null}
          {secondaryAction ? <Action action={secondaryAction} /> : null}
        </div>
      ) : null}
    </section>
  );
}

export function ContentSection({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`mx-auto max-w-2xl px-4 sm:px-6 py-10 sm:py-14 text-[14px] leading-[1.75] text-[var(--ish-text-muted)] ${className}`}
    >
      {children}
    </section>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-10 first:mt-0 mb-4 text-[22px] sm:text-[26px] font-bold leading-tight text-[var(--ish-dark)]">
      {children}
    </h2>
  );
}

interface CTASectionProps {
  eyebrow?: string;
  title: string;
  subtitle: string;
  href: string;
  cta: string;
  compact?: boolean;
}

export function CTASection({
  eyebrow,
  title,
  subtitle,
  href,
  cta,
  compact = false,
}: CTASectionProps) {
  return (
    <a
      href={href}
      className={"group block rounded-2xl border " + (compact ? "p-4" : "p-5 sm:p-6")}
      style={{
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--ish-accent) 10%, var(--ish-bg)) 0%, var(--ish-bg) 100%)",
        borderColor: "color-mix(in srgb, var(--ish-accent) 24%, transparent)",
      }}
    >
      <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--ish-accent)] text-[22px] text-white"
          aria-hidden
        >
          ॐ
        </span>
        <span className="min-w-0 flex-1">
          {eyebrow ? (
            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--ish-accent)]">
              {eyebrow}
            </span>
          ) : null}
          <span className="block text-[16px] font-bold text-[var(--ish-dark)]">{title}</span>
          <span className="mt-1 block text-[13px] leading-relaxed text-[var(--ish-text-muted)]">
            {subtitle}
          </span>
        </span>
        <span className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-xl bg-[var(--ish-accent)] px-4 text-[13px] font-bold text-white group-hover:opacity-90">
          {cta}
        </span>
      </div>
    </a>
  );
}

export function FAQSection({
  title = "Frequently Asked Questions",
  items,
}: {
  title?: string;
  items: FAQItem[];
}) {
  return (
    <div className="mt-10">
      <SectionHeading>{title}</SectionHeading>
      <div className="space-y-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-xl border border-[var(--ish-border)] bg-[var(--ish-bg-card)]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 text-[14px] font-bold text-[var(--ish-dark)]">
              <span>{item.question}</span>
              <span
                className="shrink-0 text-[18px] leading-none text-[var(--ish-accent)] transition-transform group-open:rotate-45"
                aria-hidden
              >
                +
              </span>
            </summary>
            <p className="border-t border-[var(--ish-border-light)] px-4 pb-4 pt-3 text-[14px] leading-[1.7] text-[var(--ish-text-muted)]">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}
